import { useEffect, useRef } from "react";
import Konva from "konva";
import { Group, Line, Rect, Text } from "react-konva";
import { SyncService } from "frontend/services/syncService";
import { RW } from "shared/datamodel/replicache-wrapper/mutators";
import { useAtomValue } from "jotai";
import {
  CARD_WIDTH,
  CARD_HEIGHT,
  DEFAULT_CARD_STACK_HEIGHT,
  DEFAULT_CARD_STACK_TITLE,
  DEFAULT_CARD_STACK_WIDTH,
} from "shared/datamodel/card-stack";
import { FontProperties, Point } from "shared/datamodel/schemas";
import { editingElementLinkAtom } from "state-atoms";
import { IntegrationCanvasElementInStack } from "../integration-element/integration-element";

import { ANIMATION_DURATION } from "./card-stack-utils";
import { EditableText } from "frontend/editableText";
import { noop } from "frontend/utils/fn-utils";

const HeaderHeight = 40;

export function CardStackFrame({
  x,
  y,
  width = DEFAULT_CARD_STACK_WIDTH,
  height = DEFAULT_CARD_STACK_HEIGHT,
  title = DEFAULT_CARD_STACK_TITLE,
  isEditing = false,
  onStopEditing,
  onChangeTitle = noop,
  name,
}: {
  x: number;
  y: number;
  width?: number;
  height?: number;
  title?: string;
  isEditing?: boolean;
  onStopEditing?: () => void;
  onChangeTitle?: (newValue: string) => void;
  name?: string;
}) {
  return (
    <Group x={x} y={y} name={name}>
      <Rect
        width={width}
        height={height}
        fill="#fff"
        stroke="#EBEDF3"
        strokeWidth={1}
        strokeEnabled={true}
        cornerRadius={10}
        listening={false}
        shadowEnabled={true}
        shadowBlur={10}
        shadowColor="rgba(17, 51, 87, 0.15)"
        shadowOffsetY={10}
      />
      <Line
        points={[0, HeaderHeight + 1, width, HeaderHeight + 1]}
        stroke="#EBEDF3"
        strokeWidth={1}
        listening={false}
      />
      <EditableText
        x={45}
        y={2}
        fill="#113357"
        font="Poppins"
        fontSize={14}
        fontProps={FontProperties.Bold}
        verticalAlign="middle"
        isFixedHeight={true}
        width={width - 100}
        height={HeaderHeight}
        isEditing={isEditing}
        onStopEditing={onStopEditing}
        onChange={onChangeTitle}
        text={title || ""}
        placeholder={"Add title"}
        ellipsis={true}
      />
    </Group>
  );
}

interface CardStackFooterProps {
  x: number;
  y: number;
  width: number;
  height: number;
  text: string;
  onClick: () => void;
  name?: string;
}

export function CardStackFooter(props: CardStackFooterProps) {
  const { x, y, width, height, onClick, text, name } = props;
  return (
    <Group name={name} x={x} y={y}>
      <Line points={[0, 0, width, 0]} stroke="#EBEDF3" strokeWidth={2} />
      <Text
        x={0}
        y={0}
        width={width}
        height={height}
        fontFamily="Poppins"
        fontSize={12}
        align="center"
        verticalAlign="middle"
        fill="#1973FF"
        text={text}
        onClick={onClick}
      />
    </Group>
  );
}

export function AnimatableCard({
  id,
  element,
  syncService,
  changeElement,
  target,
  initial,
  display,
}: {
  id: string;
  element: any;
  syncService?: SyncService<RW>;
  changeElement: any;
  target: Point;
  initial: Point;
  display: boolean;
}) {
  const editingElementLinkId = useAtomValue(editingElementLinkAtom);
  const ref = useRef<Konva.Group>(null);
  const posRef = useRef<Point>(initial);
  useEffect(() => {
    if (ref.current) {
      ref.current.to({
        x: target.x,
        y: target.y,
        duration: ANIMATION_DURATION,
        easing: Konva.Easings.StrongEaseOut,
        onFinish: () => (posRef.current = { ...target }),
      });
    }
  }, [target, ref.current]);

  const shape = display ? (
    <IntegrationCanvasElementInStack
      key={id}
      id={id}
      syncService={syncService}
      element={element}
      isEditingLink={editingElementLinkId == id}
      changeElement={changeElement}
    />
  ) : null;
  const p = posRef.current;
  return (
    <Group key={id} ref={ref} {...p}>
      {shape}
    </Group>
  );
}

export function ShadowCard({ x, y }: { x: number; y: number }) {
  return <Rect x={x} y={y} width={CARD_WIDTH} height={CARD_HEIGHT} fill="grey" opacity={0.1} strokeEnabled={false} />;
}
